import React, {useEffect, useRef} from 'react'
import styled from '@emotion/styled'
import {css} from '@emotion/core'
import {MdClose as Close} from 'react-icons/md'
import * as bodyScrollLock from 'body-scroll-lock'
import OutsideClickHandler from 'react-outside-click-handler'

import {hexToRGBStr} from '@daedalus/core/src/utils/string'
import ContentWrapper from '../../../designSystem/helpers/ContentWrapper'
import Portal from '../../../designSystem/helpers/Portal'
import Button from '../../../designSystem/Button'

type PopUpPropsType = {
  /** The body of the pop-up */
  children: React.ReactNode
  /** The callback for the close button click */
  closeAction?: () => void
  /** The footer of the pop-up. Commonly used for action button placement */
  footer?: React.ReactNode
  /** Whether the footer should stick to the bottom of the popup when the popup is scrolled */
  isFooterSticky?: boolean
  /** The header of the pop-up */
  header?: React.ReactNode
  /** The height of the pop-up. This will be limited by the window height. */
  height?: number
  /** Whether the pop-up is open or not */
  isOpen: boolean
  /** The width of the pop-up. This will be limited by the window width. */
  width?: number
  /** Identify the close button element for selection in integration tests, FullStory, etc. */
  closeButtonDataId?: string
}

const Wrapper = styled('div')(
  ({theme}) => css`
    position: fixed;
    z-index: 101;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    background: rgba(${hexToRGBStr(theme.colors.overlayBg)}, 0.6);

    display: flex;
    align-items: center;
    justify-content: center;
  `
)

const Content = styled('div')<{width?: number; height?: number}>(
  ({theme, width, height}) => css`
    ${height && `height: ${height}px;`}
    max-height: calc(100vh - ${theme.layout.spacing.s500}px);
    width: ${width}px;
    margin: 0 ${theme.layout.spacing.s300}px;
    border-radius: ${theme.layout.radius.md}px;
    background: ${theme.colors.defaultBg};
    display: flex;
    flex-direction: column;
    position: relative;
  `
)

const HeaderWrapper = styled('div')<{header?: React.ReactNode}>(
  ({header}) => !header && `height: 0;`
)

const Header = styled(ContentWrapper)(
  ({theme}) => css`
    ${theme.typography.heading.title2}
    margin: 0;
    text-align: center;
    position: relative;
    padding: ${theme.layout.spacing.s600}px ${theme.layout.spacing.s600}px
      ${theme.layout.spacing.s500}px;
  `
)

const CloseButton = styled(Button)(
  ({theme}) => css`
    color: ${theme.colors.popupCloseBtnColor};
    padding: ${theme.layout.spacing.s600}px ${theme.layout.spacing.s600}px
      ${theme.layout.spacing.s500}px;
    z-index: 1;
    position: absolute;
    right: 0;
    top: 0;
  `
)

const CloseIcon = styled(Close)(
  () => css`
    height: 24px;
  `
)

const BodyWrapper = styled(ContentWrapper)<{hasHeader: boolean}>(
  ({theme, hasHeader}) => css`
    padding: ${hasHeader ? 0 : theme.layout.spacing.s600}px
      ${theme.layout.spacing.s600}px ${theme.layout.spacing.s600}px;
    height: 100%;
    overflow: auto;
    display: flex;
    flex-direction: column;
  `
)

const StickyFooterWrapper = styled(ContentWrapper)(
  ({theme}) => css`
    border-top: solid 1px ${theme.colors.borderColor};
  `
)

const Body = styled('div')(
  () => css`
    flex: 0 1 auto;
  `
)

const Footer = styled('div')(
  ({theme}) => css`
    padding-top: ${theme.layout.spacing.s500}px;
    text-align: center;
  `
)

const PopUp = ({
  header,
  width = 418,
  height,
  isOpen,
  children,
  footer,
  isFooterSticky,
  closeButtonDataId,
  closeAction
}: PopUpPropsType) => {
  const popUpRef = useRef(null)
  const isCloseVisible = Boolean(closeAction)

  bodyScrollLock.enableBodyScroll(popUpRef.current)

  useEffect(() => {
    const currentRef = popUpRef.current
    if (isOpen) {
      bodyScrollLock.disableBodyScroll(currentRef)
    }

    return () => {
      bodyScrollLock.enableBodyScroll(currentRef)
    }
  }, [isOpen])

  return (
    <Portal>
      {isOpen && (
        <Wrapper>
          <OutsideClickHandler onOutsideClick={closeAction}>
            <Content width={width} height={height}>
              <HeaderWrapper header={header}>
                {header && <Header>{header}</Header>}
                {isCloseVisible && (
                  <CloseButton
                    dataId={closeButtonDataId}
                    appearance="noStyle"
                    onClick={closeAction}
                  >
                    <CloseIcon size="24" />
                  </CloseButton>
                )}
              </HeaderWrapper>

              <BodyWrapper ref={popUpRef} hasHeader={Boolean(header)}>
                <Body>{children}</Body>
                {footer && !isFooterSticky && <Footer>{footer}</Footer>}
              </BodyWrapper>

              {footer && isFooterSticky && (
                <StickyFooterWrapper>{footer}</StickyFooterWrapper>
              )}
            </Content>
          </OutsideClickHandler>
        </Wrapper>
      )}
    </Portal>
  )
}

export default PopUp
